import { CloseIcon } from '@calizahq/react-components-v2'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Transition } from 'react-transition-group'
import { useClickAway } from 'react-use'

import { useScrollLock } from '@/hooks'

import { AnimatedBackdrop, ModalContainerAnimated } from './Modal.styles'

export interface ModalProps {
  children: JSX.Element | ReactNode
  className?: string
  headerText: string
  isCloseShown?: boolean
  isShown: boolean
  onExit?(): void
  toggle(): void
}

export function Modal({
  children,
  toggle,
  isShown,
  headerText,
  isCloseShown = true,
  className = '',
  onExit,
}: ModalProps): JSX.Element | null {
  const modalRef = useRef<HTMLElement | null>(document.getElementById('modal-root') || null)
  const modalContentRef = useRef<HTMLDivElement | null>(null)
  const modalAnimatedRef = useRef<HTMLDivElement | null>(null)
  const transitionRef = useRef<HTMLDivElement | null>(null)
  const [modalLoaded, setModalLoaded] = useState(0)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [offsetFromBottom, setOffsetFromBottom] = useState(0)

  const { lockScroll, unlockScroll } = useScrollLock()

  useEffect(() => {
    if (!modalRef.current) {
      modalRef.current = document.getElementById('modal-root') || null
      setModalLoaded(modalLoaded + 1)
    }
    return () => {
      modalRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') toggle()
    },
    [toggle],
  )

  useClickAway(modalContentRef, toggle)

  useEffect(() => {
    if (isShown) {
      lockScroll()
      document.addEventListener('keydown', onKeyDown)
    } else {
      unlockScroll()
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isShown, lockScroll, unlockScroll, onKeyDown])

  function handleExit() {
    onExit && onExit()
  }

  const modalUI = (
    <Transition
      in={isShown}
      mountOnEnter={true}
      nodeRef={transitionRef}
      onExited={handleExit}
      timeout={500}
      unmountOnExit={true}
    >
      {(state) => (
        <div ref={transitionRef}>
          {state !== 'exited' && (
            <AnimatedBackdrop
              className="fixed left-0 top-0 z-30 h-screen w-screen transition-colors duration-500"
              id="animatedBackrop"
              state={state}
            />
          )}
          <span role="button" tabIndex={0} />
          <span className="sr-only">Modal Window: press escape to return to page</span>
          <div
            aria-labelledby={headerText}
            aria-modal={true}
            className={`fixed left-1/2 top-1/2 z-40 flex h-full w-fit-content -translate-x-1/2 -translate-y-1/2 transform items-end opacity-100 outline-none sm:items-center`}
            ref={modalContentRef}
            role="dialog"
            tabIndex={-1}
          >
            <ModalContainerAnimated
              className={`relative ${state}`}
              offsetFromBottom={offsetFromBottom}
              ref={modalAnimatedRef}
              state={state}
            >
              {state !== 'exited' && (
                <div
                  className={`w-screen overflow-hidden rounded-t-lg bg-primary md:w-fit-content md:rounded-lg ${className}`}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={() => {}}
                  role="presentation"
                >
                  <div className="flex items-center justify-between border-b px-8 py-5 border-secondary">
                    <p className="font-medium leading-100 text-tertiary">{headerText}</p>
                    {isCloseShown && <CloseIcon className="w-5 cursor-pointer icon-tertiary" onClick={toggle} />}
                  </div>
                  {children}
                </div>
              )}
            </ModalContainerAnimated>
          </div>
        </div>
      )}
    </Transition>
  )

  return modalRef.current ? createPortal(modalUI, modalRef.current) : null
}
